// src/pages/legal/HelpPage.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import TextPage, { Contact } from './TextPage';

function HelpPage() {
    return (
        <TextPage title="Help">
            <h2>The basics</h2>
            <ol>
                <li>
                    <strong>Create a tournament</strong> on the dashboard: a short dialog asks for the format, courts and
                    ranking/matchmaking method up front (most of this can be changed later). Then add the players (type them
                    in, pick them from earlier tournaments, or upload an Excel file with the columns &ldquo;First
                    Name&rdquo; and &ldquo;Last Name&rdquo;).
                </li>
                <li>
                    <strong>Plan a round.</strong> The app makes the pairings: who plays with and against whom, on which court.
                    With fewer courts than matchups, the players who have played most sit out first.
                </li>
                <li>
                    <strong>Requests:</strong> under &ldquo;Requests for the next round&rdquo; you can grant wishes: players who want to
                    play together (against opponents you choose, or ones the app picks) or a singles game. They are granted first and
                    the rest are paired as usual.
                </li>
                <li>
                    <strong>Enter the scores</strong> of each set (Enter or leaving the box saves). A new round can be planned
                    once the last one is scored, or its unplayed matchups are cancelled.
                </li>
                <li>
                    <strong>Watch the ranking</strong> and repeat. When you are done, mark the tournament as finished to lock
                    the results and see the podium.
                </li>
            </ol>

            <h2>Ranking and pairing methods</h2>
            <p>
                Under Settings on a tournament (More on a phone) you can open &ldquo;Change methods&rdquo; to choose how results become a ranking (for example
                rating, wins, or points) and how the next round is planned (for example balanced, or classic). Each method
                lists what it is good and bad at, and there are sliders to tune them. The ranking always shows how a
                player&rsquo;s score came about: tap a player.
            </p>

            <h2>Fixed teams</h2>
            <p>
                By default players rotate partners every round. Under Settings, &ldquo;Format&rdquo; can switch a tournament to
                &ldquo;Fixed teams&rdquo; instead: pair players up once (in the Players view, before the first round), and every
                round then pairs whole teams against each other rather than choosing new partners. Requests and the singles
                setting do not apply in this format. Teams are locked once the first round is played.
            </p>

            <h2>Groups &amp; finals</h2>
            <p>
                A third format: teams are made as above and dealt over groups at random. You say how many teams a
                group should have; the number of groups follows from how many teams you make. Teams can be moved, or the
                groups shuffled, until the first round. Each round plans the next group games; when all of them are scored, the
                best teams of every group go through to a knockout (quarterfinals, semifinals, final and, if you like, a match
                for third place), planned the same way, round by round. Knockout games need a winner. The teams per group,
                how many advance and third place are set when creating the tournament, or under Settings.
            </p>

            <h2>Sharing</h2>
            <ul>
                <li>
                    <strong>Read-only link:</strong> &ldquo;Share&rdquo; makes a link and a QR code. Participants see the
                    ranking and rounds live, find their own games with &ldquo;Find me&rdquo;, and you can show the
                    &ldquo;Big screen&rdquo; view on a TV.
                </li>
                <li>
                    <strong>Co-organizers:</strong> &ldquo;Invite&rdquo; makes a link for people who should help run the
                    tournament. They can do everything except delete it and manage the list.
                </li>
            </ul>

            <h2>Scores, history and results</h2>
            <ul>
                <li>
                    <strong>History</strong> (under More on a phone) lists the latest changes of scores: the players, who made
                    the change, when, and what the score was before. To correct a score, edit it in the round.
                </li>
                <li>
                    <strong>Final ranking</strong> shows the podium and can be saved as a picture (to send around) or as a
                    spreadsheet (CSV).
                </li>
            </ul>

            <h2>Without a connection</h2>
            <p>
                After you sign in, the app prepares itself for offline use. Scores, players and rounds then work
                without a connection and are sent to the server when you are back online. If you changed the same thing in two
                places, the app asks which value to keep and merges everything else. Without an account (&ldquo;Continue
                without an account&rdquo;) everything stays on your device.
            </p>

            <h2>Your data</h2>
            <p>
                Backups are made automatically and can be restored under &ldquo;Backups&rdquo; on the dashboard. You can
                manage your name, password and email address, download a copy of all your data, and delete your account, on the <Link to="/account">account page</Link>. More in the{' '}
                <Link to="/privacy">privacy policy</Link>.
            </p>

            <h2>Something wrong?</h2>
            <p>
                Reload the app first (a new version may be waiting). If it still misbehaves, tell us what you did and what
                you saw: <Contact />.
            </p>
        </TextPage>
    );
}

export default HelpPage;
